import { useCallback, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Home,
  ListChecks,
  Plug,
  Play,
  Settings,
  Terminal,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/* ─────────────────────────── Types ─────────────────────────── */

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PaletteItem {
  id: string;
  label: string;
  icon: LucideIcon;
  section: 'Pages' | 'Commands';
  /** Route to navigate to (pages) or action string (commands). */
  action: string;
  /** Optional keyboard shortcut hint displayed on the right. */
  shortcut?: string;
}

/* ──────────────────────── Item registry ────────────────────── */

const ITEMS: PaletteItem[] = [
  // Pages
  { id: 'page-home', label: 'Home', icon: Home, section: 'Pages', action: '/' },
  { id: 'page-play', label: 'Play', icon: Play, section: 'Pages', action: '/play' },
  { id: 'page-tasks', label: 'Tasks', icon: ListChecks, section: 'Pages', action: '/tasks' },
  { id: 'page-connections', label: 'Connections', icon: Plug, section: 'Pages', action: '/connections' },
  { id: 'page-memory', label: 'Memory', icon: Brain, section: 'Pages', action: '/memory' },
  { id: 'page-workflows', label: 'Workflows', icon: Workflow, section: 'Pages', action: '/workflows' },
  { id: 'page-settings', label: 'Settings', icon: Settings, section: 'Pages', action: '/settings' },

  // Commands
  { id: 'cmd-remember', label: '/remember', icon: Terminal, section: 'Commands', action: '/remember' },
  { id: 'cmd-workflow', label: '/workflow', icon: Terminal, section: 'Commands', action: '/workflow' },
  { id: 'cmd-connect', label: '/connect', icon: Terminal, section: 'Commands', action: '/connect' },
  { id: 'cmd-help', label: '/help', icon: Terminal, section: 'Commands', action: '/help' },
];

/* ────────────────────────── Component ──────────────────────── */

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  /* Filter items by query */
  const filtered = useMemo(() => {
    if (!query.trim()) return ITEMS;
    const lower = query.toLowerCase();
    return ITEMS.filter((item) => item.label.toLowerCase().includes(lower));
  }, [query]);

  /* Group filtered items by section preserving order */
  const sections = useMemo(() => {
    const map = new Map<string, PaletteItem[]>();
    for (const item of filtered) {
      const group = map.get(item.section) ?? [];
      group.push(item);
      map.set(item.section, group);
    }
    return map;
  }, [filtered]);

  /* Execute the selected item */
  const execute = useCallback(
    (item: PaletteItem) => {
      if (item.section === 'Pages') {
        navigate(item.action);
      }
      // Commands could dispatch events or navigate to play with prefill —
      // for now we navigate to /play (chat) as a sensible default.
      if (item.section === 'Commands') {
        navigate('/play');
      }
      onOpenChange(false);
    },
    [navigate, onOpenChange],
  );

  /* Keyboard navigation */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[activeIndex];
        if (item) execute(item);
      }
    },
    [filtered, activeIndex, execute],
  );

  /* Reset state when dialog opens/closes */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        setQuery('');
        setActiveIndex(0);
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  /* Scroll active item into view */
  const scrollActiveIntoView = useCallback(
    (index: number) => {
      if (!listRef.current) return;
      const items = listRef.current.querySelectorAll('[data-palette-item]');
      items[index]?.scrollIntoView({ block: 'nearest' });
    },
    [],
  );

  /* Track index changes for scroll */
  const setActiveAndScroll = useCallback(
    (index: number) => {
      setActiveIndex(index);
      // schedule scroll after state update
      requestAnimationFrame(() => scrollActiveIntoView(index));
    },
    [scrollActiveIntoView],
  );

  /* Flat index counter for rendering */
  let flatIndex = -1;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[rgba(15,19,17,0.5)] animate-[fadeIn_200ms_ease-out_both]" />

        {/* Panel */}
        <Dialog.Content
          className="fixed inset-0 z-50 flex justify-center pointer-events-none"
          onKeyDown={handleKeyDown}
          aria-label="Command palette"
        >
          <div className="max-w-[520px] w-full mx-auto mt-[20vh] pointer-events-auto h-fit rounded-2xl bg-wayne-surface border border-wayne-hairline shadow-2xl animate-[modalIn_200ms_ease-out_both]">
            {/* Search input */}
            <div className="border-b border-wayne-hairline">
              <input
                type="text"
                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                placeholder="Type a command or search..."
                className="w-full px-5 py-4 text-[15px] bg-transparent text-wayne-ink placeholder:text-wayne-ink-3 outline-none"
              />
            </div>

            {/* Results list */}
            <div ref={listRef} className="max-h-[360px] overflow-y-auto p-2">
              {filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-[13px] text-wayne-ink-3">
                  No results found.
                </p>
              )}

              {Array.from(sections.entries()).map(([section, items]) => (
                <div key={section} className="mb-1">
                  {/* Section eyebrow label */}
                  <p className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-wayne-ink-3">
                    {section}
                  </p>

                  {items.map((item) => {
                    flatIndex += 1;
                    const isActive = flatIndex === activeIndex;
                    const currentIndex = flatIndex;
                    const Icon = item.icon;

                    return (
                      <button
                        key={item.id}
                        data-palette-item
                        type="button"
                        className={`flex items-center gap-3 w-full px-3 py-2 text-left rounded-lg transition-colors ${
                          isActive ? 'bg-wayne-surface-2' : ''
                        }`}
                        onMouseEnter={() => setActiveAndScroll(currentIndex)}
                        onClick={() => execute(item)}
                      >
                        <Icon size={16} className="shrink-0 text-wayne-ink-3" />
                        <span className="flex-1 text-[14px] text-wayne-ink">{item.label}</span>
                        {item.shortcut && (
                          <span className="bg-wayne-surface-2 border border-wayne-hairline rounded px-1.5 py-0.5 font-mono text-[10px] text-wayne-ink-3">{item.shortcut}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Footer keyboard hints */}
            <div className="border-t border-wayne-hairline px-5 py-3">
              <p className="text-[11px] text-wayne-ink-3">
                &uarr;&darr; Navigate &middot; Enter Select &middot; Esc Close
              </p>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
